import { Component, Inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { AUserPermissionsService, EmailProfilesService, handleError, IEmailProfileHolder } from "@coso-replicator/core";
import { ViewMode } from "@coso/forms";
import { suppressTabbingToNextCell, TemplateRendererComponent } from "@coso/grid";
import { LoadingService } from "@coso/loading";
import { DialogComponent, DialogType, IS_SAAS } from "@coso/utility";
import { GridOptions } from "ag-grid-community";
import { AddEditEmailNotificationProfileComponent } from "./add-edit-email-notification-profile/add-edit-email-notification-profile.component";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { AddEditEmailNotificationProfileSaaSComponent } from "./add-edit-saas-email-notification-profile/add-edit-email-notification-profile-saas.component";

@Component({
  standalone:false,
  templateUrl: "./email-notification-profiles.component.html",
  styleUrls: ["./email-notification-profiles.component.scss"]
})
export class EmailNotificationProfilesComponent implements OnInit {
  @ViewChild("actionButtons", { static: true }) actionButtons: TemplateRef<any>;
  ViewMode = ViewMode;
  modalRef: NgbModalRef;
  gridOptions: GridOptions;
  profiles: IEmailProfileHolder[];
  errorMessage: string;
  successMessage: string;
  isReadOnly: boolean;

  constructor(
    @Inject(IS_SAAS) public isSaaS: boolean,
    public loadingService: LoadingService,
    private userPermissionsService: AUserPermissionsService,
    private emailProfilesService: EmailProfilesService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.userPermissionsService.hasLimitedAccess$.subscribe((hasLimitedAccess) => (this.isReadOnly = hasLimitedAccess));
    this.gridOptions = {
      rowHeight: 40,
      suppressRowClickSelection: true,
      onFirstDataRendered: (e) => {
        e.api.setGridAriaProperty("label", "List of Email Notification Profiles");
      },
      onGridReady: (event) => {
        this.userPermissionsService.isCustomerAgent$.subscribe((isCustomerAgent) => {
          event.api.setColumnsVisible(["tenantId"], isCustomerAgent);
          event.api.sizeColumnsToFit();
        });
      },
      defaultColDef: { sortable: true, resizable: true, minWidth: 100, suppressKeyboardEvent: suppressTabbingToNextCell },
      columnDefs: [
        { headerName: "Name", field: "name" },
        { headerName: "Tenant ID", field: "tenantId"},
        {
          headerName: "Actions",
          sortable: false,
          resizable: false,
          maxWidth: 110,
          minWidth: 110,
          cellRenderer: TemplateRendererComponent,
          cellRendererParams: { ngTemplate: this.actionButtons }
        }
      ]
    };
    this.loadEmailProfiles();
  }

  openDeleteModal(entity: IEmailProfileHolder) {
    this.errorMessage = this.successMessage = undefined;
    const modalRef = this.modalService.open(DialogComponent, {
      keyboard: false,
      ariaLabelledBy: "modalTitle"
    });
    modalRef.componentInstance.options = {
      titleText: "Delete email notification profile",
      type: DialogType.Warning,
      contentText: `Are you sure you want to delete email notification profile "${entity.name}"?`,
      rightButtons: [
        { label: "No", type: "button", class: "btn btn-dark mr-2", onClick: (modalRef) => modalRef.dismiss() },
        {
          label: "Yes",
          type: "button",
          class: "btn btn-primary",
          onClick: (modalRef) => {
            this.loadingService.start(`Deleting email notification profile`);
            this.emailProfilesService.delete(entity.name).subscribe(
              () => {
                this.successMessage = `Email notification profile "${entity.name}" has been deleted successfully.`;
                this.loadEmailProfiles();
                this.loadingService.stop();
                modalRef.dismiss();
              },
              (error) => {
                this.errorMessage = `Failed to delete email notification profile due to: ${handleError(error)}`;
                this.loadingService.stop();
              }
            );
          }
        }
      ],
      onClose: (modalRef) => modalRef.dismiss()
    };
  }

  openAddEditModal(holder?: IEmailProfileHolder) {
    this.modalRef = this.modalService.open(this.isSaaS ? AddEditEmailNotificationProfileSaaSComponent : AddEditEmailNotificationProfileComponent, {
      ariaLabelledBy: "modalTitle",
      keyboard: true,
      backdrop: "static",
      modalDialogClass: "modal-lg"
    });
    this.modalRef.componentInstance.viewMode = holder ? (this.isReadOnly ? ViewMode.View : ViewMode.Edit) : ViewMode.Create;
    this.modalRef.componentInstance.entity = holder;
    this.modalRef.componentInstance.saveCallback = holder ? this.update.bind(this, holder.name) : this.create.bind(this);
    this.modalRef.componentInstance.sendTestEmailCallback = this.sendTestEmail.bind(this);
  }

  private sendTestEmail(entity: IEmailProfileHolder) {
    this.modalRef.componentInstance.successMessage = this.modalRef.componentInstance.errorMessage = undefined;

    this.loadingService.start("Sending a test email", "sendTestEmail");
    this.emailProfilesService.testConnection(entity).subscribe(
      (data) => {
        if (data.success) {
          this.modalRef.componentInstance.successMessage = "Test email has been sent successfully.";
        } else {
          this.modalRef.componentInstance.errorMessage = `Failed to send a test email due to: ${data.error}`;
        }
        this.loadingService.stop("sendTestEmail");
      },
      (error) => {
        this.modalRef.componentInstance.errorMessage = `Failed to send a test email due to: ${handleError(error)}`;
        this.loadingService.stop("sendTestEmail");
      }
    );
  }

  private update(name: string, entity: IEmailProfileHolder) {
    this.loadingService.start("Updating email notification profile");
    this.emailProfilesService.update(name, entity).subscribe(
      () => {
        this.modalRef?.close();
        this.successMessage = `Email notification profile "${entity.name}" has been successfully updated.`;
        this.loadEmailProfiles();
        this.loadingService.stop();
      },
      (error) => {
        this.modalRef.componentInstance.errorMessage = `Failed to update email notification profile due to: ${handleError(error)}`;
        this.loadingService.stop();
      }
    );
  }

  private create(entity: IEmailProfileHolder) {
    this.loadingService.start("Creating database connection");
    this.emailProfilesService.create(entity).subscribe(
      () => {
        this.modalRef?.close();
        this.successMessage = `Email notification profile "${entity.name}" has been successfully created.`;
        this.loadEmailProfiles();
        this.loadingService.stop();
      },
      (error) => {
        this.modalRef.componentInstance.errorMessage = `Failed to create email notification profile due to: ${handleError(error)}`;
        this.loadingService.stop();
      }
    );
  }

  private loadEmailProfiles() {
    this.loadingService.start("Loading email profiles", "loadEmailProfiles");
    this.emailProfilesService.get().subscribe(
      (profiles) => {
        this.profiles = profiles;
        this.loadingService.stop("loadEmailProfiles");
      },
      (error) => {
        this.errorMessage = `Failed to load email profiles due to: ${handleError(error)}`;
        this.loadingService.stop("loadEmailProfiles");
      }
    );
  }
}
